import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Select,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { DateInput, DateTimePicker, TimeInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { pdf } from "@react-pdf/renderer";
import { IconChevronDown, IconClock, IconPrinter, IconX } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import moment from "moment";
import { PDFDocument } from "pdf-lib";
import { decrypt } from "../../utils";
import EMSFormPDF from "../EMSFormPDF";
import {
  ATTACHMENT_OPTIONS,
  CODE_STATUS_OPTIONS,
  DEVICE_OPTIONS,
  DIRECTIVE_LOCATION_OPTIONS,
  EMPTY,
  ISOLATION_OPTIONS,
  NOT_DOCUMENTED,
  TIME_FIELDS,
  TRANSPORT_OPTIONS,
  VITAL_FIELDS,
  DATE_TIME_FORMAT,
  buildEmsSheetData,
  defaultAllergies,
  defaultMedicaidId,
  defaultOtherPayerId,
  emsRecordId,
  formatDate,
  getInitials,
  resolveOrg,
} from "../EMSFormPDF/emsSheetData";
import useStyles from "./style";

const DEFAULT_ATTACHMENTS = {
  advancedDirectives: true,
  faceSheet: true,
  hipaaRelease: true,
  currentMar: true,
  guardianshipOrder: false,
};

const DEFAULT_DEVICES = {
  Eyeglasses: false,
  Dentures: false,
  "Hearing aid": false,
  "Walker or cane": false,
  Wheelchair: false,
  Oxygen: false,
};

const safeDecrypt = (value) => {
  if (!value) return null;
  try {
    return decrypt(value) || null;
  } catch {
    return value;
  }
};

const CheckTile = ({ checked, onToggle, label, hint, required, classes }) => (
  <Box
    className={classes.check}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle();
      }
    }}
  >
    <Box className={`${classes.checkBox} ${checked ? classes.checkBoxActive : ""}`}>
      ✓
    </Box>
    <Text component="span" className={classes.checkText}>
      {label}
      {required ? <em>*</em> : null}
      {hint ? (
        <Text component="small" className={classes.checkHint}>
          {hint}
        </Text>
      ) : null}
    </Text>
  </Box>
);

const ChartPanel = ({ title, aside, items, empty, conflict, classes }) => (
  <Box className={`${classes.chart} ${conflict ? classes.chartConflict : ""}`}>
    <Box className={classes.chartHead}>
      <span>{title}</span>
      {aside ? <span>{aside}</span> : null}
    </Box>
    {items.length ? (
      <Box component="ul" className={classes.chartList}>
        {items.map((item) => (
          <Box component="li" key={item} className={classes.chartItem}>
            {item}
          </Box>
        ))}
      </Box>
    ) : (
      <Text className={classes.chartEmpty}>{empty}</Text>
    )}
  </Box>
);

const EMSFormModal = ({
  opened,
  onClose,
  clientDetails,
  profile,
  onDownloadComplete,
  onCompleteIncident,
}) => {
  const { classes } = useStyles();
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const timeRefs = useRef({});

  const [stage, setStage] = useState("form");
  const [showErrors, setShowErrors] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [blankLoading, setBlankLoading] = useState(false);
  const [packet, setPacket] = useState(null);

  /* critical */
  const [codeStatus, setCodeStatus] = useState(null);
  const [directiveSignedOn, setDirectiveSignedOn] = useState(null);
  const [directiveLocation, setDirectiveLocation] = useState(
    DIRECTIVE_LOCATION_OPTIONS[0]
  );
  const [allergiesReviewed, setAllergiesReviewed] = useState(false);
  const [noKnownAllergies, setNoKnownAllergies] = useState(false);
  const [isolationStatus, setIsolationStatus] = useState("");
  const [weight, setWeight] = useState("");
  const [room, setRoom] = useState("");

  /* clinical */
  const [medicationsVerified, setMedicationsVerified] = useState(false);
  const [deviceSelections, setDeviceSelections] = useState(DEFAULT_DEVICES);

  /* coverage */
  const [medicaidId, setMedicaidId] = useState("");
  const [otherPayerId, setOtherPayerId] = useState("");
  const [facilityLicense, setFacilityLicense] = useState("");
  const [facilityPhone, setFacilityPhone] = useState("");

  /* at time of emergency */
  const [emergencyNotes, setEmergencyNotes] = useState("");
  const [times, setTimes] = useState({});
  const [vitals, setVitals] = useState({});
  const [didNotGoToHospital, setDidNotGoToHospital] = useState(false);
  const [hospitalDestination, setHospitalDestination] = useState("");
  const [transportedBy, setTransportedBy] = useState(TRANSPORT_OPTIONS[0]);

  /* notification */
  const [authorizedRepNotified, setAuthorizedRepNotified] = useState(false);
  const [authorizedRepresentative, setAuthorizedRepresentative] = useState("");
  const [authorizedRepContactedBy, setAuthorizedRepContactedBy] = useState("");
  const [notificationDate, setNotificationDate] = useState(null);

  /* attachments + attestation */
  const [attachmentChecks, setAttachmentChecks] = useState(DEFAULT_ATTACHMENTS);
  const [completedBy, setCompletedBy] = useState("");
  const [completedAt, setCompletedAt] = useState(null);
  const [attested, setAttested] = useState(false);

  const org = useMemo(() => resolveOrg(clientDetails, profile), [clientDetails, profile]);
  const chartAllergies = useMemo(() => defaultAllergies(clientDetails), [clientDetails]);

  const resetForm = () => {
    const now = new Date();
    setStage("form");
    setShowErrors(false);
    setPacket(null);
    setCodeStatus(null);
    setDirectiveSignedOn(null);
    setDirectiveLocation(DIRECTIVE_LOCATION_OPTIONS[0]);
    setAllergiesReviewed(false);
    setNoKnownAllergies(false);
    setIsolationStatus("");
    setWeight(`${clientDetails?.form1?.weight || ""}`);
    setRoom(`${clientDetails?.form1?.roomNumber || ""}`);
    setMedicationsVerified(false);
    setDeviceSelections(DEFAULT_DEVICES);
    setMedicaidId(defaultMedicaidId(clientDetails));
    setOtherPayerId(defaultOtherPayerId(clientDetails));
    setFacilityLicense(org.facilityLicense);
    setFacilityPhone(org.facilityPhone);
    setEmergencyNotes("");
    setTimes({});
    setVitals({});
    setDidNotGoToHospital(false);
    setHospitalDestination("");
    setTransportedBy(TRANSPORT_OPTIONS[0]);
    setAuthorizedRepNotified(false);
    setAuthorizedRepresentative(clientDetails?.form2?.emergencyContactName || "");
    setAuthorizedRepContactedBy(profile?.user?.name || "");
    setNotificationDate(now);
    setAttachmentChecks(DEFAULT_ATTACHMENTS);
    setCompletedBy(profile?.user?.name || "");
    setCompletedAt(now);
    setAttested(false);
  };

  useEffect(() => {
    if (!opened) return;
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, clientDetails?.id, profile?.user?.name]);

  // The generated packet is held as an object URL so "Open the printed sheet"
  // and a mobile print both reuse the same bytes instead of re-rendering.
  useEffect(
    () => () => {
      if (packet?.url) URL.revokeObjectURL(packet.url);
    },
    [packet?.url]
  );

  if (!clientDetails) return null;

  const allergyConflict = noKnownAllergies && chartAllergies.length > 0;

  const errors = {
    codeStatus: !codeStatus ? "Choose the client's code status." : null,
    allergies: allergyConflict
      ? "The chart lists allergies, so this cannot be recorded as none. Untick one."
      : !allergiesReviewed && !noKnownAllergies
        ? "Confirm the allergy list, or record that there are none."
        : null,
    isolationStatus: !isolationStatus ? "Select the isolation status." : null,
    emergencyNotes:
      emergencyNotes.trim().length < 4
        ? "Describe why EMS was requested."
        : null,
    attested: !attested ? "Attestation is required before the packet is generated." : null,
  };

  const missing = [
    !codeStatus ? "code status" : null,
    !allergiesReviewed && !noKnownAllergies ? "allergy review" : null,
    !isolationStatus ? "isolation status" : null,
    emergencyNotes.trim().length < 4 ? "reason for EMS" : null,
    !attested ? "attestation" : null,
    allergyConflict ? "allergy conflict" : null,
  ].filter(Boolean);

  const ready = missing.length === 0;
  const fieldError = (key) => (showErrors ? errors[key] : null);

  const emsFormData = {
    /* keys the incident report reads back */
    emergencyNotes,
    staffAndDate: [completedBy, formatDate(completedAt)].filter(Boolean).join(" - "),
    hospitalDestination,
    didNotGoToHospital,
    authorizedRepNotified,
    authorizedRepresentative,
    authorizedRepContactedBy,
    notificationDate,
    attachmentChecks,

    /* the new sheet */
    codeStatus,
    directiveSignedOn,
    directiveLocation,
    allergiesReviewed,
    noKnownAllergies,
    isolationStatus,
    weight,
    room,
    medicationsVerified,
    deviceSelections,
    medicaidId,
    otherPayerId,
    facilityLicense,
    facilityPhone,
    times,
    vitals,
    transportedBy,
    completedBy,
    completedAt,
    attested,
  };

  const sheet = buildEmsSheetData({ clientDetails, profile, formData: emsFormData });
  const clientImage = safeDecrypt(clientDetails?.imageurl);

  const handleGeneratePacket = async () => {
    if (!ready) {
      setShowErrors(true);
      return;
    }
    setDownloadLoading(true);
    try {
      const clientName = clientDetails?.name || "client";
      const dateStamp = moment().format("YYYY-MM-DD");
      // The transfer sheet is the whole packet. The client summary used to be
      // appended here, but it prints "Allergies: None" for an empty chart, which
      // is exactly the inference this sheet exists to avoid. Face sheet is now a
      // paper checkbox like the rest of the document list.
      const emsBlob = await pdf(
        <EMSFormPDF
          clientData={clientDetails}
          profile={profile}
          formData={emsFormData}
        />
      ).toBlob();

      const rendered = await PDFDocument.load(await emsBlob.arrayBuffer());
      const fileName = `${clientName.replace(/\s+/g, "_")}_EMS_Packet_${dateStamp}.pdf`;

      saveAs(emsBlob, fileName);
      setPacket({
        url: URL.createObjectURL(emsBlob),
        pages: rendered.getPageCount(),
        fileName,
      });
      setStage("done");
      onDownloadComplete?.(emsFormData);
    } catch (error) {
      console.error("EMS packet generation error:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Ungated on purpose. Nothing has been entered yet, so the required field
  // check must never stand between staff and a printable binder copy.
  //
  // "Blank" means blank of incident data, not blank of the client. Everything
  // the chart already answers is printed, and only what staff record at the
  // time of transfer is left as a write-on line, so the binder copy is worth
  // reaching for in an emergency.
  const handleBlankPacket = async () => {
    setBlankLoading(true);
    try {
      const prefill = {
        weight: `${clientDetails?.form1?.weight || ""}`,
        room: `${clientDetails?.form1?.roomNumber || ""}`,
        medicaidId: defaultMedicaidId(clientDetails),
        otherPayerId: defaultOtherPayerId(clientDetails),
        facilityLicense: org.facilityLicense,
        facilityPhone: org.facilityPhone,
      };
      const blob = await pdf(
        <EMSFormPDF
          clientData={clientDetails}
          profile={profile}
          formData={prefill}
          blank
        />
      ).toBlob();
      const clientName = (clientDetails?.name || "client").replace(/\s+/g, "_");
      saveAs(
        blob,
        `${clientName}_EMS_Transfer_Sheet_${moment().format("YYYY-MM-DD")}.pdf`
      );
    } catch (error) {
      console.error("EMS blank sheet error:", error);
    } finally {
      setBlankLoading(false);
    }
  };

  const openPrintedSheet = () => {
    if (!packet?.url) return;
    // Mobile browsers have no print dialog for a blob, so the tab itself is the
    // fallback: same PDF, opened in the platform viewer.
    const tab = window.open(packet.url, "_blank");
    if (!tab) saveAs(packet.url, packet.fileName);
  };

  const setTimeValue = (key, value) =>
    setTimes((current) => ({ ...current, [key]: value }));

  const setVitalValue = (key, value) =>
    setVitals((current) => ({ ...current, [key]: value }));

  const chevron = <IconChevronDown size={16} stroke={1.8} />;

  const displayMeds = sheet.medications.slice(0, 4);
  const clientMeta = [
    sheet.client.sex,
    sheet.client.dobWithAge ? sheet.client.dobWithAge : null,
    room ? `Room ${room}` : null,
    sheet.recordId,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobileScreen ? "100%" : 700}
      radius={isMobileScreen ? 0 : 13}
      overlayProps={{ blur: 3 }}
      withCloseButton={false}
      className={classes.drawer}
      zIndex={1000}
    >
      <Box className={classes.head}>
        <Text className={classes.headTitle}>
          {stage === "done" ? "EMS Form" : "Add EMS Form"}
        </Text>
        {stage === "form" ? (
          <Button
            variant="default"
            className={classes.blankBtn}
            leftIcon={<IconPrinter size={15} stroke={1.9} />}
            onClick={handleBlankPacket}
            loading={blankLoading}
            title="Print a sheet filled in from the chart, ready to complete by hand"
          >
            Print blank
          </Button>
        ) : null}
        <Button
          variant="default"
          className={classes.closeBtn}
          onClick={onClose}
          aria-label="Close"
          ml={stage === "done" ? "auto" : 0}
        >
          <IconX size={17} stroke={2} />
        </Button>
      </Box>

      {stage === "done" ? (
        <>
          <Box className={classes.body}>
            <Box className={classes.done}>
              <Box className={classes.doneIcon}>✓</Box>
              <Text className={classes.doneTitle}>EMS packet generated</Text>
              <Text className={classes.doneText}>
                Saved to the client record. The transfer sheet downloaded and is
                ready to hand to the responding crew.
              </Text>
            </Box>

            <Box className={classes.receipt}>
              <table>
                <tbody>
                  <tr>
                    <td>Record</td>
                    <td>{emsRecordId(clientDetails)}</td>
                  </tr>
                  <tr>
                    <td>Client</td>
                    <td>{clientDetails?.name || EMPTY}</td>
                  </tr>
                  <tr>
                    <td>Code status</td>
                    <td>{codeStatus || NOT_DOCUMENTED}</td>
                  </tr>
                  <tr>
                    <td>Completed by</td>
                    <td>{completedBy || EMPTY}</td>
                  </tr>
                  <tr>
                    <td>Signed</td>
                    <td>
                      {completedAt
                        ? moment(completedAt).format(DATE_TIME_FORMAT)
                        : EMPTY}
                    </td>
                  </tr>
                  <tr>
                    <td>Pages</td>
                    <td>{packet?.pages || EMPTY}</td>
                  </tr>
                </tbody>
              </table>
            </Box>

            <Box className={classes.doneActions}>
              <Button
                className={`${classes.doneAction} ${classes.doneActionPrimary}`}
                onClick={openPrintedSheet}
              >
                Open the printed sheet
              </Button>
              <Button
                className={classes.doneAction}
                onClick={() => onCompleteIncident?.(emsFormData)}
              >
                Complete incident report
              </Button>
              <Button className={classes.doneAction} onClick={() => setStage("form")}>
                Amend this form
              </Button>
              <Button className={classes.doneAction} onClick={onClose}>
                Done
              </Button>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box className={classes.body}>
            <Box className={classes.who}>
              <Box className={classes.avatar}>
                {clientImage ? (
                  <img src={clientImage} alt="client" className={classes.avatarImage} />
                ) : (
                  getInitials(clientDetails?.name)
                )}
              </Box>
              <Box>
                <Text className={classes.whoName}>{clientDetails?.name || EMPTY}</Text>
                <Text className={classes.whoMeta}>{clientMeta || EMPTY}</Text>
              </Box>
            </Box>

            {/* ------------------------------------------------- critical */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>
                Critical status
                <Text component="span" className={classes.badgeRequired}>
                  Required
                </Text>
              </Box>
              <Text className={classes.hint}>
                Printed at the top of the sheet. The crew reads this first.
              </Text>

              <Text component="span" className={classes.fieldLabel}>
                Code status <em>*</em>
              </Text>
              <Box className={classes.radioGroup}>
                {CODE_STATUS_OPTIONS.map((option) => (
                  <Box
                    key={option}
                    role="radio"
                    aria-checked={codeStatus === option}
                    tabIndex={0}
                    className={`${classes.radioTile} ${
                      codeStatus === option ? classes.radioTileActive : ""
                    }`}
                    onClick={() => setCodeStatus(option)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setCodeStatus(option);
                      }
                    }}
                  >
                    <Box
                      className={`${classes.radioDot} ${
                        codeStatus === option ? classes.radioDotActive : ""
                      }`}
                    />
                    {option}
                  </Box>
                ))}
              </Box>
              {fieldError("codeStatus") ? (
                <Text className={classes.errorText}>{fieldError("codeStatus")}</Text>
              ) : null}

              {codeStatus === "DNR / POLST" ? (
                <Box
                  mt={12}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isMobileScreen ? "1fr" : "1fr 1fr",
                    gap: 12,
                  }}
                >
                  <DateInput
                    label="Directive signed on"
                    placeholder="Select date"
                    valueFormat="MM/DD/YYYY"
                    value={directiveSignedOn}
                    onChange={setDirectiveSignedOn}
                    maxDate={new Date()}
                    clearable
                    className={classes.input}
                  />
                  <Select
                    label="Form location"
                    data={DIRECTIVE_LOCATION_OPTIONS}
                    value={directiveLocation}
                    onChange={setDirectiveLocation}
                    rightSection={chevron}
                    className={classes.input}
                  />
                </Box>
              ) : null}

              <Box mt={16}>
                <Text component="span" className={classes.fieldLabel}>
                  Allergies <em>*</em>
                </Text>
                <ChartPanel
                  classes={classes}
                  conflict={allergyConflict}
                  title="From chart"
                  aside={
                    chartAllergies.length
                      ? `${chartAllergies.length} on record`
                      : "Nothing recorded"
                  }
                  items={chartAllergies}
                  empty="Blank in the chart. Complete the client record, or record below that there are none."
                />
                <Box className={classes.checkList}>
                  <CheckTile
                    classes={classes}
                    checked={allergiesReviewed}
                    onToggle={() => setAllergiesReviewed((value) => !value)}
                    label="I confirmed this allergy list with the client record just now"
                    hint="The sheet prints the review date from this confirmation."
                  />
                  <CheckTile
                    classes={classes}
                    checked={noKnownAllergies}
                    onToggle={() => setNoKnownAllergies((value) => !value)}
                    label="No known allergies"
                    hint="Only tick this when the chart affirmatively records none. A blank chart is not the same thing."
                  />
                </Box>
                {fieldError("allergies") ? (
                  <Text className={classes.errorText}>{fieldError("allergies")}</Text>
                ) : null}
              </Box>

              <Box
                mt={16}
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "1fr 1fr",
                  gap: 12,
                }}
              >
                <Select
                  label="Isolation or airborne precautions"
                  withAsterisk
                  placeholder="Select"
                  data={ISOLATION_OPTIONS}
                  value={isolationStatus}
                  onChange={(value) => setIsolationStatus(value || "")}
                  rightSection={chevron}
                  error={fieldError("isolationStatus")}
                  className={`${classes.input} ${
                    fieldError("isolationStatus") ? classes.inputError : ""
                  }`}
                />
                <TextInput
                  label="Weight"
                  placeholder="Enter weight"
                  value={weight}
                  onChange={(event) => setWeight(event.currentTarget.value)}
                  rightSection={<span>lb</span>}
                  rightSectionWidth={38}
                  className={classes.input}
                />
              </Box>
            </Box>

            {/* ------------------------------------------------- clinical */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>
                Clinical snapshot
                <Text component="span" className={classes.badgeSource}>
                  From chart
                </Text>
              </Box>
              <Text className={classes.hint}>
                Pulled from diagnoses and the MAR. Not editable here. Fix it at the
                source.
              </Text>

              <ChartPanel
                classes={classes}
                title="Active diagnoses"
                aside={`${sheet.diagnoses.length}`}
                items={sheet.diagnoses}
                empty="No diagnoses on the client record. Complete the health form to have them print."
              />

              <Box className={classes.chart}>
                <Box className={classes.chartHead}>
                  <span>Active medications</span>
                  <span>{sheet.medications.length}</span>
                </Box>
                {sheet.medications.length ? (
                  <>
                    <Box component="table" className={classes.miniTable}>
                      <thead>
                        <tr>
                          <th style={{ width: "42%" }}>Medication</th>
                          <th style={{ width: "22%" }}>Strength</th>
                          <th style={{ width: "18%" }}>Dose</th>
                          <th>Freq</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayMeds.map((med, index) => (
                          <tr key={`${med.name}-${index}`}>
                            <td className={classes.miniName}>
                              {med.name}
                              {med.narcotic ? (
                                <Text component="span" className={classes.narcotic}>
                                  C-II
                                </Text>
                              ) : null}
                            </td>
                            <td>{med.strength || EMPTY}</td>
                            <td>{med.dose || EMPTY}</td>
                            <td>{med.frequency || EMPTY}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                    <Text className={classes.hint} mt={8} mb={0}>
                      {sheet.medications.length > displayMeds.length
                        ? `Plus ${
                            sheet.medications.length - displayMeds.length
                          } more. All ${sheet.medications.length} print on the sheet.`
                        : `All ${sheet.medications.length} print on the sheet.`}
                    </Text>
                  </>
                ) : (
                  <Text className={classes.chartEmpty}>
                    No active medications on the client record.
                  </Text>
                )}
              </Box>

              <Box className={classes.checkList}>
                <CheckTile
                  classes={classes}
                  checked={medicationsVerified}
                  onToggle={() => setMedicationsVerified((value) => !value)}
                  label="Medication list matches the current MAR"
                  hint="Unticked prints a warning on the sheet telling the crew to confirm with the pharmacy."
                />
              </Box>

              <Box mt={12}>
                <Text component="span" className={classes.fieldLabel}>
                  Assistive devices travelling with the client
                </Text>
                <Box className={classes.checkListTwo}>
                  {DEVICE_OPTIONS.map((label) => (
                    <CheckTile
                      key={label}
                      classes={classes}
                      checked={!!deviceSelections[label]}
                      onToggle={() =>
                        setDeviceSelections((current) => ({
                          ...current,
                          [label]: !current[label],
                        }))
                      }
                      label={label}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* ------------------------------------------------- coverage */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>Coverage and facility</Box>
              <Text className={classes.hint}>
                The receiving hospital asks for all of these. The sheet prints{" "}
                {org.facilityName || "the client's facility"} as the sending facility.
              </Text>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "1fr 1fr",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Medicaid ID"
                  placeholder="Enter Medicaid ID"
                  value={medicaidId}
                  onChange={(event) => setMedicaidId(event.currentTarget.value)}
                  className={classes.input}
                />
                <TextInput
                  label="Medicare or other payer"
                  placeholder="Enter payer and ID"
                  value={otherPayerId}
                  onChange={(event) => setOtherPayerId(event.currentTarget.value)}
                  className={classes.input}
                />
                <TextInput
                  label="Facility license"
                  placeholder="Enter license number"
                  value={facilityLicense}
                  onChange={(event) => setFacilityLicense(event.currentTarget.value)}
                  className={classes.input}
                />
                <TextInput
                  label="Facility 24 hour phone"
                  placeholder="Enter phone number"
                  value={facilityPhone}
                  onChange={(event) => setFacilityPhone(event.currentTarget.value)}
                  className={classes.input}
                />
                <TextInput
                  label="Room"
                  placeholder="Enter room"
                  value={room}
                  onChange={(event) => setRoom(event.currentTarget.value)}
                  className={classes.input}
                />
              </Box>
            </Box>

            {/* -------------------------------------- at time of emergency */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>
                At time of emergency
                <Text component="span" className={classes.badgeRequired}>
                  Required
                </Text>
              </Box>
              <Text className={classes.hint}>
                Fill what you know now. The printed sheet keeps write-on lines for the
                rest.
              </Text>

              <Textarea
                label="Reason EMS was requested"
                withAsterisk
                placeholder="What happened, when it started, what you observed"
                minRows={4}
                autosize
                maxRows={12}
                value={emergencyNotes}
                onChange={(event) => setEmergencyNotes(event.currentTarget.value)}
                error={fieldError("emergencyNotes")}
                className={`${classes.input} ${classes.textarea} ${
                  fieldError("emergencyNotes") ? classes.inputError : ""
                }`}
              />

              <Box
                mt={12}
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr 1fr" : "repeat(4, 1fr)",
                  gap: 12,
                }}
              >
                {TIME_FIELDS.map((field) => (
                  <TimeInput
                    key={field.key}
                    label={field.label}
                    ref={(node) => {
                      timeRefs.current[field.key] = node;
                    }}
                    value={times[field.key] || ""}
                    onChange={(event) =>
                      setTimeValue(field.key, event.currentTarget.value)
                    }
                    rightSection={
                      <IconClock
                        size={15}
                        style={{ cursor: "pointer" }}
                        onClick={() => timeRefs.current[field.key]?.showPicker?.()}
                      />
                    }
                    className={classes.input}
                  />
                ))}
              </Box>

              <Text component="span" className={classes.fieldLabel} mt={14}>
                Vitals at transfer
              </Text>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                  gap: 12,
                }}
              >
                {VITAL_FIELDS.map((field) => (
                  <TextInput
                    key={field.key}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={vitals[field.key] || ""}
                    onChange={(event) =>
                      setVitalValue(field.key, event.currentTarget.value)
                    }
                    rightSection={<span>{field.unit}</span>}
                    rightSectionWidth={54}
                    className={classes.input}
                  />
                ))}
              </Box>

              <Box className={classes.checkList} mt={12} mb={12}>
                <CheckTile
                  classes={classes}
                  checked={didNotGoToHospital}
                  onToggle={() => setDidNotGoToHospital((value) => !value)}
                  label="Client was not transported"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "1fr 1fr",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Receiving hospital"
                  placeholder="Hospital name and address"
                  value={hospitalDestination}
                  disabled={didNotGoToHospital}
                  onChange={(event) =>
                    setHospitalDestination(event.currentTarget.value)
                  }
                  className={`${classes.input} ${
                    didNotGoToHospital ? classes.inputDisabled : ""
                  }`}
                />
                <Select
                  label="Transported by"
                  data={TRANSPORT_OPTIONS}
                  value={transportedBy}
                  onChange={(value) => setTransportedBy(value || "")}
                  disabled={didNotGoToHospital}
                  rightSection={chevron}
                  className={`${classes.input} ${
                    didNotGoToHospital ? classes.inputDisabled : ""
                  }`}
                />
              </Box>
            </Box>

            {/* --------------------------------------------- notification */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>Representative notification</Box>
              <Box className={classes.checkList} mb={12}>
                <CheckTile
                  classes={classes}
                  checked={authorizedRepNotified}
                  onToggle={() => setAuthorizedRepNotified((value) => !value)}
                  label="Authorized representative advised of the destination"
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "repeat(3, 1fr)",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Representative or POA"
                  placeholder="Enter name"
                  value={authorizedRepresentative}
                  onChange={(event) =>
                    setAuthorizedRepresentative(event.currentTarget.value)
                  }
                  className={classes.input}
                />
                <TextInput
                  label="Contacted by"
                  placeholder="Enter name"
                  value={authorizedRepContactedBy}
                  onChange={(event) =>
                    setAuthorizedRepContactedBy(event.currentTarget.value)
                  }
                  className={classes.input}
                />
                <DateTimePicker
                  label="Date and time"
                  placeholder="Select date and time"
                  valueFormat={DATE_TIME_FORMAT}
                  value={notificationDate}
                  onChange={setNotificationDate}
                  clearable
                  className={classes.input}
                />
              </Box>
            </Box>

            {/* ---------------------------------------------- attachments */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>Documents sent with the client</Box>
              <Box className={classes.checkList}>
                {ATTACHMENT_OPTIONS.map((item) => (
                  <CheckTile
                    key={item.key}
                    classes={classes}
                    checked={!!attachmentChecks[item.key]}
                    onToggle={() =>
                      setAttachmentChecks((current) => ({
                        ...current,
                        [item.key]: !current[item.key],
                      }))
                    }
                    label={item.label}
                    hint={item.hint}
                  />
                ))}
              </Box>
            </Box>

            {/* ---------------------------------------------- attestation */}
            <Box className={classes.card}>
              <Box className={classes.sectionTitle}>
                Attestation
                <Text component="span" className={classes.badgeRequired}>
                  Required
                </Text>
              </Box>
              <Box className={`${classes.warn} ${ready ? classes.warnOk : ""}`}>
                <Box className={`${classes.warnIcon} ${ready ? classes.warnIconOk : ""}`}>
                  {ready ? "✓" : "!"}
                </Box>
                <Text className={classes.warnText}>
                  {ready
                    ? "Complete. This form is saved to the record and can be reprinted or amended later."
                    : "Anything left blank prints as Not documented. It is never filled in for you and never prints as None."}
                </Text>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: isMobileScreen ? "1fr" : "1fr 1fr",
                  gap: 12,
                }}
              >
                <TextInput
                  label="Completed by"
                  placeholder="Enter your name and title"
                  value={completedBy}
                  onChange={(event) => setCompletedBy(event.currentTarget.value)}
                  className={classes.input}
                />
                <DateTimePicker
                  label="Date and time"
                  placeholder="Select date and time"
                  valueFormat={DATE_TIME_FORMAT}
                  value={completedAt}
                  onChange={setCompletedAt}
                  clearable
                  className={classes.input}
                />
              </Box>
              <Box className={classes.checkList} mt={4}>
                <CheckTile
                  classes={classes}
                  required
                  checked={attested}
                  onToggle={() => setAttested((value) => !value)}
                  label="I completed this form and the information is accurate as of now"
                  hint="Saved to the client record with your name and a timestamp."
                />
              </Box>
              {fieldError("attested") ? (
                <Text className={classes.errorText}>{fieldError("attested")}</Text>
              ) : null}
            </Box>
          </Box>

          <Box className={classes.foot}>
            <Text className={`${classes.gate} ${ready ? classes.gateDone : ""}`}>
              {ready
                ? "Ready. Saves to the client record, then downloads."
                : `${missing.length} required item${
                    missing.length > 1 ? "s" : ""
                  } left: ${missing.join(", ")}`}
            </Text>
            <Box className={classes.actions}>
              <Button className={classes.cancelBtn} variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                className={classes.saveBtn}
                onClick={handleGeneratePacket}
                disabled={!ready}
                loading={downloadLoading}
              >
                {downloadLoading ? "Generating..." : "Generate EMS packet"}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default EMSFormModal;
